import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, Users, Send, CheckCircle, AlertTriangle } from "lucide-react";

const ROLES = [
  { value: "student", label: "Student" },
  { value: "instructor", label: "Instructor" },
  { value: "guardian", label: "Guardian" },
  { value: "spectator", label: "Spectator" },
  { value: "custom", label: "Custom Role" },
];

export default function AdminEnroll() {
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [singleForm, setSingleForm] = useState({ name: "", email: "", role: "student", course_id: "none", group_id: "none" });
  const [bulkEmails, setBulkEmails] = useState("");
  const [bulkRole, setBulkRole] = useState("student");
  const [bulkCourse, setBulkCourse] = useState("none");
  const [bulkGroup, setBulkGroup] = useState("none");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/courses"), api.get("/groups")]).then(([c, g]) => {
      setCourses(c.data);
      setGroups(g.data);
    });
  }, []);

  const filteredGroups = singleForm.course_id && singleForm.course_id !== "none"
    ? groups.filter((g) => g.course_id === singleForm.course_id)
    : groups;

  const bulkFilteredGroups = bulkCourse && bulkCourse !== "none"
    ? groups.filter((g) => g.course_id === bulkCourse)
    : groups;

  const handleSingle = async () => {
    if (!singleForm.name || !singleForm.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/users/create", {
        name: singleForm.name,
        email: singleForm.email,
        role: singleForm.role,
        course_id: singleForm.course_id === "none" ? null : singleForm.course_id,
        group_id: singleForm.group_id === "none" ? null : singleForm.group_id,
      });
      toast.success(`${data.name} enrolled! Login details sent to ${data.email}`);
      setSingleForm({ name: "", email: "", role: "student", course_id: singleForm.course_id, group_id: singleForm.group_id });
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  const handleBulk = async () => {
    const emails = bulkEmails.split(/[\n,;]+/).map((e) => e.trim()).filter((e) => e && e.includes("@"));
    if (emails.length === 0) { toast.error("Enter at least one valid email"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/users/bulk-create", {
        emails,
        role: bulkRole,
        course_id: bulkCourse === "none" ? null : bulkCourse,
        group_id: bulkGroup === "none" ? null : bulkGroup,
      });
      setResults(data);
      toast.success(`${data.created} accounts created, ${data.skipped} skipped`);
      setBulkEmails("");
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-enroll" className="space-y-6 animate-fade-in-up max-w-3xl">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Enrol Users</h1>
          <p className="text-[#2B2B2B]/50 mt-1 text-sm">Create accounts directly — login credentials are emailed automatically</p>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2">
          <button onClick={() => setMode("single")} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${mode === "single" ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]" : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"}`}>
            <UserPlus className="w-4 h-4 inline mr-1.5" />Single
          </button>
          <button onClick={() => setMode("bulk")} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${mode === "bulk" ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]" : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"}`}>
            <Users className="w-4 h-4 inline mr-1.5" />Bulk Enrol
          </button>
        </div>

        {/* Single Enrol */}
        {mode === "single" && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8 space-y-5 animate-fade-in-up">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Full Name</label>
                <Input data-testid="enroll-name" value={singleForm.name} onChange={(e) => setSingleForm({ ...singleForm, name: e.target.value })} placeholder="e.g., Maria Borg" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Email</label>
                <Input data-testid="enroll-email" type="email" value={singleForm.email} onChange={(e) => setSingleForm({ ...singleForm, email: e.target.value })} placeholder="student@email.com" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Role</label>
                <Select value={singleForm.role} onValueChange={(v) => setSingleForm({ ...singleForm, role: v })}>
                  <SelectTrigger data-testid="enroll-role" className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                <Select value={singleForm.course_id} onValueChange={(v) => setSingleForm({ ...singleForm, course_id: v, group_id: "none" })}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Group</label>
                <Select value={singleForm.group_id} onValueChange={(v) => setSingleForm({ ...singleForm, group_id: v })}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Assign later</SelectItem>
                    {filteredGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="enroll-submit" onClick={handleSingle} disabled={submitting} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11 hover:shadow-[0_4px_16px_rgb(92,58,46,0.25)] transition-all duration-200">
              <Send className="w-4 h-4 mr-2" /> {submitting ? "Creating..." : "Create Account & Send Credentials"}
            </Button>
          </div>
        )}

        {/* Bulk Enrol */}
        {mode === "bulk" && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8 space-y-5 animate-fade-in-up">
            <div>
              <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Email Addresses</label>
              <Textarea data-testid="bulk-emails" value={bulkEmails} onChange={(e) => setBulkEmails(e.target.value)} rows={6} placeholder={"student1@email.com\nstudent2@email.com\nstudent3@email.com\n\nPaste one email per line, or comma-separated"} className="rounded-xl border-[#EADBC8] resize-none font-mono text-xs" />
              <p className="text-xs text-[#2B2B2B]/40 mt-1">{bulkEmails.split(/[\n,;]+/).filter((e) => e.trim() && e.includes("@")).length} valid emails detected</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Role</label>
                <Select value={bulkRole} onValueChange={setBulkRole}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                <Select value={bulkCourse} onValueChange={(v) => { setBulkCourse(v); setBulkGroup("none"); }}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Group</label>
                <Select value={bulkGroup} onValueChange={setBulkGroup}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Assign later</SelectItem>
                    {bulkFilteredGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="bulk-submit" onClick={handleBulk} disabled={submitting} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11 hover:shadow-[0_4px_16px_rgb(92,58,46,0.25)] transition-all duration-200">
              <Users className="w-4 h-4 mr-2" /> {submitting ? "Creating accounts..." : "Create All Accounts & Send Credentials"}
            </Button>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 animate-scale-in">
            <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold mb-3">Results</h3>
            <div className="space-y-2">
              {results.results.map((r, i) => (
                <div key={`result-${i}`} className={`flex items-center gap-3 p-3 rounded-xl ${r.status === "created" ? "bg-[#5C3A2E]/5" : "bg-red-50"}`}>
                  {r.status === "created" ? <CheckCircle className="w-4 h-4 text-[#5C3A2E]" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                  <span className="text-sm text-[#2B2B2B] flex-1">{r.email}</span>
                  <span className="text-xs text-[#2B2B2B]/40">{r.status === "created" ? `✓ Created as ${r.name}` : r.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
